import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home as HomeIcon,
  KanbanSquare,
  CalendarClock,
  Building2,
  HandCoins,
  Megaphone,
  MessageSquare,
  Package,
  BarChart3,
  Users as UsersIcon,
  ScrollText,
  Upload,
  Sparkles,
  LogOut,
  Search,
  Shield,
  Trophy,
  TrendingUp,
  History,
  KeyRound,
  Calendar as CalendarIcon,
  UserPlus,
  FileText,
  Wrench,
  BookOpen,
  Activity,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import InstallAppButton from "@/components/InstallAppButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import FAB from "@/components/FAB";
import GlobalSearch from "@/components/GlobalSearch";
import { useState } from "react";

const navItems = [
  { to: "/", label: "Home", icon: HomeIcon, perm: null },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon, perm: "view_followups" },
  { sectionLabel: "Customers" },
  { to: "/leads", label: "Leads", icon: UserPlus, perm: "view_clients", indent: true },
  { to: "/clients", label: "Clients", icon: Building2, perm: "view_clients", indent: true },
  { to: "/contacts", label: "Contacts", icon: UsersIcon, perm: "view_contacts", indent: true },
  { to: "/accounts", label: "Accounts", icon: Building2, perm: "view_clients", indent: true },
  { to: "/all-contacts", label: "All Contacts", icon: BookOpen, perm: "view_clients", indent: true },
  { sectionLabel: "Sales" },
  // Pipeline is now a parent with three sub-pipelines. The "to" of the parent
  // is the default landing (Product) so existing bookmarks keep working.
  {
    to: "/pipeline/product",
    label: "Pipeline",
    icon: KanbanSquare,
    perm: "view_deals",
    children: [
      { to: "/pipeline/product",  label: "Product",            icon: KanbanSquare, perm: "view_deals" },
      { to: "/pipeline/service",  label: "Technical Services", icon: KanbanSquare, perm: "view_deals" },
      { to: "/pipeline/training", label: "Training",           icon: KanbanSquare, perm: "view_deals" },
    ],
  },
  { to: "/service-execution", label: "Service Execution", icon: KanbanSquare, perm: "view_deals" },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock, perm: "view_followups" },
  { to: "/deals", label: "Deals", icon: HandCoins, perm: "view_deals" },
  { to: "/quotations", label: "Quotations", icon: FileText, perm: "view_deals" },
  { to: "/products", label: "Products", icon: Package, perm: "view_products" },
  { sectionLabel: "Tools" },
  { to: "/ai-extract", label: "AI Extract", icon: Sparkles, perm: "use_ai_extract" },
  { to: "/marketing", label: "Marketing", icon: Megaphone, perm: null },
  { to: "/feedback", label: "Feedback", icon: MessageSquare, perm: null },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "view_reports" },
];

const adminItems = [
  { to: "/performance", label: "Performance Hub", icon: TrendingUp, perm: "manage_users", adminOnly: true },
  { to: "/team", label: "Team Pulse", icon: Trophy, perm: "manage_users", adminOnly: true },
  { to: "/admin/activity", label: "Admin Activity", icon: Activity, perm: "manage_users", adminOnly: true },
  { to: "/users", label: "Users", icon: UsersIcon, perm: "manage_users" },
  { to: "/import-export", label: "Import / Export", icon: Upload, perm: "bulk_import" },
  { to: "/audit-logs", label: "Audit Logs", icon: ScrollText, perm: "view_audit_logs" },
  { to: "/login-history", label: "Login History", icon: History, perm: "view_audit_logs" },
  { to: "/settings", label: "Security", icon: Shield, perm: "manage_users" },
];

function hasPerm(user, perm) {
  if (!perm) return true;
  return (user?.permissions || []).includes(perm);
}

function initials(name = "") {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2 sm:gap-3 tap shrink-0" data-testid="brand-link">
      <img
        src="https://customer-assets.emergentagent.com/job_deal-discipline/artifacts/gq30gt0t_RENVIR%20%283%29.png"
        alt="RENVIRO SYSTEMS"
        className="h-12 sm:h-20 lg:h-24 w-auto select-none shrink-0 -my-1"
        draggable="false"
      />
      <span className="hidden md:inline-block text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-bold border-l border-border pl-3">
        Sales Memory
      </span>
    </Link>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  const visibleNav = navItems.filter((n) => n.sectionLabel || hasPerm(user, n.perm));
  const visibleAdmin = adminItems.filter((n) => hasPerm(user, n.perm) && (!n.adminOnly || user?.role === "admin"));

  return (
    <div className="min-h-screen bg-background overflow-x-clip">
      {/* Top bar — sticky glass.

          NOTE: The wrapper uses `overflow-x-clip` (NOT `overflow-x-hidden`)
          — `overflow-x-hidden` on an ancestor breaks `position: sticky` in
          iOS Safari/WebKit. `overflow-x-clip` gives the same horizontal
          clipping without freezing sticky descendants. This bug caused the
          top bar to scroll away under the iOS notch on PWA users. */}
      <header
        className="sticky top-0 z-40 glass border-b border-border/60"
        data-testid="top-bar"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-24 lg:h-28 flex items-center justify-between gap-2 sm:gap-4">
          <Brand />
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="hidden sm:flex items-center gap-2 px-4 h-10 rounded-2xl bg-muted/60 border border-border/60 text-muted-foreground hover:bg-muted transition tap min-w-[280px]"
            data-testid="global-search-trigger"
          >
            <Search className="h-4 w-4" />
            <span className="text-sm">Search clients, deals, contacts…</span>
            <kbd className="ml-auto text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded-md bg-background border border-border">
              ⌘K
            </kbd>
          </button>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="sm:hidden h-10 w-10 grid place-items-center rounded-2xl bg-muted/60 border border-border/60 tap"
              data-testid="global-search-trigger-mobile"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            <InstallAppButton />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 h-10 px-2 pr-3 rounded-2xl hover:bg-muted/70 tap" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary-100 text-primary-700 font-semibold text-xs">
                      {initials(user?.name || user?.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:flex flex-col items-start leading-tight">
                    <span className="text-sm font-semibold">{user?.name || user?.email}</span>
                    <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-bold uppercase tracking-wider">
                      {user?.role}
                    </Badge>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl" data-testid="user-menu-content">
                <DropdownMenuLabel className="font-display">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="rounded-xl"
                  onClick={() => navigate("/account")}
                  data-testid="account-button"
                >
                  <KeyRound className="h-4 w-4 mr-2" />
                  Account & sessions
                </DropdownMenuItem>
                {visibleAdmin.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Admin</DropdownMenuLabel>
                    {visibleAdmin.map((n) => {
                      const Icon = n.icon;
                      return (
                        <DropdownMenuItem
                          key={n.to}
                          className="rounded-xl"
                          onClick={() => navigate(n.to)}
                          data-testid={`menu-${n.to.replace(/\//g, "")}`}
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {n.label}
                        </DropdownMenuItem>
                      );
                    })}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="rounded-xl"
                  onClick={() => {
                    logout().then(() => navigate("/login", { replace: true }));
                  }}
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r border-border/60 px-3 py-5 sticky top-28 h-[calc(100vh-7rem)]">
          <nav className="flex-1 space-y-0.5">
            <p className="px-3 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Workspace</p>
            {visibleNav.map((it, idx) => {
              if (it.sectionLabel) {
                return (
                  <p key={`s-${idx}`} className="px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
                    {it.sectionLabel}
                  </p>
                );
              }
              const Active = location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to.split("/").slice(0, 2).join("/")));
              return (
                <div key={it.to}>
                  <NavLink
                    to={it.to}
                    data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={() =>
                      `group flex items-center gap-2.5 ${it.indent ? "pl-5 pr-3" : "px-3"} h-10 rounded-2xl text-sm font-semibold transition tap ${
                        Active
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-foreground/70 hover:bg-muted hover:text-foreground"
                      }`
                    }
                  >
                    <it.icon className="h-4 w-4" />
                    <span>{it.label}</span>
                  </NavLink>
                  {/* Sub-pipeline children — only shown when the parent or any
                      sibling pipeline route is active to keep the sidebar tidy. */}
                  {it.children && Active && (
                    <div className="ml-6 mt-1 mb-1 space-y-0.5">
                      {it.children.map((c) => {
                        const sub = location.pathname === c.to;
                        return (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            data-testid={`subnav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={() =>
                              `flex items-center gap-2 px-3 h-8 rounded-xl text-xs font-semibold transition tap ${
                                sub
                                  ? "bg-primary-50 text-primary-800 ring-1 ring-primary/20"
                                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
                              }`
                            }
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                            <span>{c.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {visibleAdmin.length > 0 && (
              <>
                <p className="px-3 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Admin</p>
                {visibleAdmin.map((it) => {
                  const Active = location.pathname.startsWith(it.to);
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={() =>
                        `group flex items-center gap-2.5 px-3 h-10 rounded-2xl text-sm font-semibold transition tap ${
                          Active
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-foreground/70 hover:bg-muted hover:text-foreground"
                        }`
                      }
                    >
                      <it.icon className="h-4 w-4" />
                      <span>{it.label}</span>
                    </NavLink>
                  );
                })}
              </>
            )}
          </nav>
          <div className="mt-4 px-3 py-3 rounded-2xl bg-primary-50 border border-primary-100">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-primary-700">Discipline</p>
            <p className="mt-1 text-xs text-primary-800 leading-snug">
              Update memory in &lt;60 seconds. Never miss a follow-up.
            </p>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 pb-24 lg:pb-10">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-border/60"
        data-testid="bottom-nav"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-5 max-w-[600px] mx-auto">
          {[
            navItems[0],          // Home
            { to: "/clients", label: "Customers", icon: Building2, perm: "view_clients", _altPaths: ["/leads", "/all-contacts"] },
            // Service-role staff don't run the sales kanban — they execute
            // jobs. Swap their middle tab for the Service Execution board
            // so their primary worklist is one tap away. Everyone else
            // keeps the Pipeline tab.
            user?.role === "service"
              ? { to: "/service-execution", label: "Service Exec", icon: Wrench, perm: "view_deals" }
              : navItems.find((n) => n.to === "/pipeline/product"),
            navItems.find((n) => n.to === "/followups"),
            // Admin: show Performance Hub tile instead of AI Extract — admins
            // don't run extractions themselves. The non-admin still needs
            // quick voice-journal access so they keep AI Extract.
            user?.role === "admin"
              ? { to: "/performance", label: "Performance", icon: TrendingUp, perm: null }
              : navItems.find((n) => n.to === "/ai-extract"),
          ].map((it) => {
            if (!it) return null;
            if (!hasPerm(user, it.perm)) return <div key={it.to} />;
            // Pipeline tab uses `/pipeline/product` as its primary `to`, but
            // we want the tab to stay highlighted on `/pipeline/service` and
            // `/pipeline/training` too. Inject those alt paths here so we
            // don't have to mutate the shared `navItems` config.
            const altPaths = it._altPaths || (it.to === "/pipeline/product" ? ["/pipeline"] : []);
            const Active = location.pathname === it.to
              || (it.to !== "/" && location.pathname.startsWith(it.to))
              || altPaths.some((p) => location.pathname.startsWith(p));
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`bottom-nav-${it.label.toLowerCase()}`}
                className={() =>
                  `flex flex-col items-center justify-center gap-1 h-14 transition tap ${
                    Active ? "text-primary" : "text-muted-foreground"
                  }`
                }
              >
                <it.icon className="h-[18px] w-[18px]" strokeWidth={2.4} />
                <span className="text-[10px] font-semibold tracking-wide">{it.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <FAB />
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
